import type { ExtraHeadersProvider, LanguageProvider } from "@roastery/api-client";
import type { ReactNode } from "react";
import { AuthProvider as OidcAuthProvider } from "react-oidc-context";
import { ApiProvider } from "./api-context";
import { cleanupSigninUrl, createOidcSettings, type OidcOptions } from "./oidc";

export interface RoasteryAuthProviderProps extends OidcOptions {
  /** Base URL of roastery-core, e.g. http://api.localhost. */
  apiBaseUrl: string;
  children: ReactNode;
  /** Headers every request carries beyond the token — `X-Support-Session` in roastery-app (US-14.3). */
  getExtraHeaders?: ExtraHeadersProvider;
  /**
   * The language the screens are being read in, sent as `Accept-Language` (US-23.4). Without it the
   * server answers from the browser's own preference, which is a different question — and it decides
   * a product's description, a validation message and a problem detail.
   */
  getLanguage?: LanguageProvider;
}

/**
 * Single entry point for authentication: the OIDC session plus an API client bound to it. Wrap the
 * application once, then use {@link useAuth}, {@link useApi} and {@link useCurrentUser} anywhere below.
 */
export function RoasteryAuthProvider({
  apiBaseUrl,
  children,
  getExtraHeaders,
  getLanguage,
  ...oidcOptions
}: RoasteryAuthProviderProps) {
  return (
    <OidcAuthProvider {...createOidcSettings(oidcOptions)} onSigninCallback={cleanupSigninUrl}>
      <ApiProvider baseUrl={apiBaseUrl} getExtraHeaders={getExtraHeaders} getLanguage={getLanguage}>
        {children}
      </ApiProvider>
    </OidcAuthProvider>
  );
}
